import {
	BadgeCheckIcon,
	ChevronDownIcon,
	Clock,
	EllipsisVerticalIcon,
	PlusIcon,
	RotateCcw,
	Trash2Icon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import type { SelectOptionConfig } from '@/lib/option-generators';
import { Editable } from '@/components/ui/Editable';
import { VirtualSelect } from '@/components/ui/virtual-select';
import { cn } from '@/lib/utils';

type MetricVisibility = 'visible' | 'hidden';

type SectionMetric = {
	id: string;
	name: string;
	unit?: string;
	icon: ReactNode;
	visibility: MetricVisibility;
};

type StepMetric = {
	id: string;
	name: string;
	unit?: string;
	isSelected: boolean;
};

type StepSet = {
	id: string;
	order: number;
	values: Record<string, string>;
};

type StepPlan = {
	id: string;
	order: number;
	movement: string;
	equipment: string;
	note?: string;
	mode: 'template' | 'custom';
	primaryMuscle: string;
	metrics: StepMetric[];
	sets: StepSet[];
	isExpanded?: boolean;
};

type SectionPlan = {
	id: string;
	name: string;
	sectionNumber: number;
	sectionType: string;
	rounds: number;
	duration: string;
	metrics: SectionMetric[];
	steps: StepPlan[];
};

const sectionTypeOptions: SelectOptionConfig<string>[] = [
	{ value: 'standard', label: 'Standard' },
	{ value: 'emom', label: 'EMOM' },
	{ value: 'amrap', label: 'AMRAP' },
	{ value: 'circuit', label: 'Circuit' },
	{ value: 'superset', label: 'Superset' },
];

const sectionPlan: SectionPlan = {
	id: 'section-1',
	name: 'Lower Body Strength',
	sectionNumber: 1,
	sectionType: 'Standard',
	rounds: 4,
	duration: '45 min',
	metrics: [
		{
			id: 'reps',
			name: 'Reps',
			icon: <BadgeCheckIcon className="size-3" />,
			visibility: 'visible',
		},
		{
			id: 'load',
			name: 'Load',
			unit: 'kg',
			icon: <BadgeCheckIcon className="size-3" />,
			visibility: 'visible',
		},
		{
			id: 'tempo',
			name: 'Tempo',
			icon: <BadgeCheckIcon className="size-3" />,
			visibility: 'visible',
		},
		{ id: 'rir', name: 'RIR', icon: <BadgeCheckIcon className="size-3" />, visibility: 'hidden' },
	],
	steps: [
		{
			id: 'step-1',
			order: 0,
			movement: 'Barbell Back Squat',
			equipment: 'Power Rack',
			mode: 'custom',
			primaryMuscle: 'Quads',
			metrics: [
				{ id: 'reps', name: 'Reps', isSelected: true },
				{ id: 'load', name: 'Load', unit: 'kg', isSelected: true },
				{ id: 'tempo', name: 'Tempo', isSelected: true },
				{ id: 'duration', name: 'Duration', unit: 's', isSelected: false },
				{ id: 'rep-pace', name: 'Rep Pace', isSelected: false },
				{ id: 'distance', name: 'Distance', unit: 'm', isSelected: false },
			],
			sets: [
				{
					id: 'set-1',
					order: 0,
					values: { reps: '5', load: '90', tempo: '30X1' },
				},
				{
					id: 'set-2',
					order: 1,
					values: { reps: '5', load: '95', tempo: '30X1' },
				},
				{
					id: 'set-3',
					order: 2,
					values: { reps: '5', load: '100', tempo: '32X1' },
				},
			],
			isExpanded: true,
		},
		{
			id: 'step-2',
			order: 1,
			movement: 'Romanian Deadlift',
			equipment: 'Barbell',
			note: 'Brace hard before lowering. No touch-and-go.',
			mode: 'template',
			primaryMuscle: 'Hamstrings',
			metrics: [
				{ id: 'reps', name: 'Reps', isSelected: true },
				{ id: 'load', name: 'Load', unit: 'kg', isSelected: true },
				{ id: 'tempo', name: 'Tempo', isSelected: true },
				{ id: 'duration', name: 'Duration', unit: 's', isSelected: false },
				{ id: 'rep-pace', name: 'Rep Pace', isSelected: false },
				{ id: 'distance', name: 'Distance', unit: 'm', isSelected: false },
			],
			sets: [
				{
					id: 'set-1',
					order: 0,
					values: { reps: '8', load: '70', tempo: '30X1' },
				},
				{
					id: 'set-2',
					order: 1,
					values: { reps: '8', load: '72.5', tempo: '30X1' },
				},
			],
			isExpanded: false,
		},
	],
};

type ConfigurationItem = {
	id: string;
	label: string;
	value: string;
};

type ConfigurationVariant = {
	id: string;
	name: string;
	render: (items: ConfigurationItem[]) => ReactNode;
};

const getConfigurationItems = (section: SectionPlan): ConfigurationItem[] => [
	{ id: 'rounds', label: 'Rounds', value: String(section.rounds) },
	{ id: 'timeCap', label: 'Time Cap', value: section.duration },
	{ id: 'rest', label: 'Rest', value: '60 sec' },
];

const configurationVariants: ConfigurationVariant[] = [
	{
		id: 'variant-01',
		name: 'Minimal Outline',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-left text-[0.6rem] text-gray-500 transition-colors hover:border-gray-300 sm:flex-row sm:items-center sm:justify-between sm:text-[0.65rem]"
					>
						<span className="font-medium uppercase tracking-wide">{item.label}</span>
						<span className="truncate font-semibold text-[0.8rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-02',
		name: 'Soft Fill',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-transparent bg-gray-100 px-2 py-1.5 text-left text-[0.6rem] text-gray-600 transition-colors hover:bg-gray-200/80 sm:flex-row sm:items-center sm:justify-between sm:text-[0.65rem]"
					>
						<span className="font-medium uppercase tracking-wide">{item.label}</span>
						<span className="truncate rounded-sm bg-white px-1.5 py-0.5 font-semibold text-[0.7rem] text-gray-900 shadow-sm sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-03',
		name: 'Underline Accent',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-transparent px-2 py-1.5 text-left text-[0.58rem] text-gray-500 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="font-semibold uppercase tracking-wide">{item.label}</span>
						<span className="w-full border-gray-200 border-b pb-0.5 font-semibold text-[0.8rem] text-gray-900 sm:w-auto sm:border-0 sm:p-0">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-04',
		name: 'Inset Field',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-left text-[0.6rem] text-gray-500 shadow-inner transition-colors hover:border-gray-300 sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="font-semibold uppercase tracking-wide">{item.label}</span>
						<span className="truncate rounded-sm border border-gray-200 px-1.5 py-0.5 font-semibold text-[0.7rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-05',
		name: 'Accent Bar',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="relative flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-left text-[0.6rem] text-gray-500 transition-colors hover:border-gray-300 sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="-translate-y-1/2 absolute top-1/2 left-1 h-5 w-0.5 rounded-full bg-gray-300 sm:static sm:h-full sm:w-0.5 sm:translate-y-0" />
						<span className="pl-1 font-semibold uppercase tracking-wide sm:pl-2">{item.label}</span>
						<span className="truncate font-semibold text-[0.78rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-06',
		name: 'Soft Shadow',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-transparent bg-white px-2 py-1.5 text-left text-[0.6rem] text-gray-600 shadow-sm transition-all hover:shadow sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="font-medium uppercase tracking-wide">{item.label}</span>
						<span className="truncate font-semibold text-[0.8rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-07',
		name: 'Dashed Outline',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-gray-300 border-dashed px-2 py-1.5 text-left text-[0.6rem] text-gray-500 transition-colors hover:border-gray-400 sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="font-semibold uppercase tracking-wide">{item.label}</span>
						<span className="truncate font-semibold text-[0.78rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-08',
		name: 'Muted Pill',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-left text-[0.58rem] text-gray-500 transition-colors hover:border-gray-300 sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="font-semibold uppercase tracking-[0.14em]">{item.label}</span>
						<span className="truncate font-semibold text-[0.78rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-09',
		name: 'Glass Tile',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-white/60 bg-white/70 px-2 py-1.5 text-left text-[0.6rem] text-gray-500 backdrop-blur transition-colors hover:border-gray-200 sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="font-medium uppercase tracking-wide">{item.label}</span>
						<span className="truncate font-semibold text-[0.8rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-10',
		name: 'Divider Row',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item, itemIndex) => (
					<button
						key={item.id}
						type="button"
						className={cn(
							'flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-left text-[0.6rem] text-gray-500 transition-colors hover:border-gray-300 sm:flex-row sm:items-center sm:justify-between',
							itemIndex !== 0 && 'sm:border-l sm:pl-3',
						)}
					>
						<span className="font-semibold uppercase tracking-wide">{item.label}</span>
						<span className="truncate font-semibold text-[0.8rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-11',
		name: 'Elevated',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-transparent bg-white px-2 py-1.5 text-left text-[0.6rem] text-gray-600 shadow-sm ring-1 ring-gray-100 ring-inset transition-all hover:shadow-md sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="font-medium uppercase tracking-wide">{item.label}</span>
						<span className="truncate font-semibold text-[0.8rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
	{
		id: 'variant-12',
		name: 'Tag Row',
		render: (items) => (
			<div className="flex gap-1">
				{items.map((item) => (
					<button
						key={item.id}
						type="button"
						className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-left text-[0.58rem] text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-100 sm:flex-row sm:items-center sm:justify-between"
					>
						<span className="font-semibold uppercase tracking-[0.16em]">{item.label}</span>
						<span className="truncate rounded-full bg-white px-1.5 py-0.5 font-semibold text-[0.72rem] text-gray-900 sm:text-right">
							{item.value}
						</span>
					</button>
				))}
			</div>
		),
	},
];

function SectionHeader({ section }: { section: SectionPlan }) {
	const [sectionName, setSectionName] = useState(section.name);

	const handleSectionTypeChange = (newOption: SelectOptionConfig<string> | null) => {
		// TODO: Connect to actual section type update logic
		console.log('Section type changed to:', newOption?.value);
	};

	return (
		<header className="relative rounded-t-xl border border-gray-200 bg-white px-4 pt-4 pb-3.5 sm:px-5 sm:pt-5 sm:pb-4">
			{/* Three-dot menu - absolutely positioned */}
			<button
				type="button"
				className="absolute top-4 right-4 flex size-8 flex-shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors active:bg-gray-100 sm:top-5 sm:right-5 sm:size-9"
			>
				<EllipsisVerticalIcon className="size-4.5 sm:size-5" />
			</button>

			<div className="flex flex-col gap-5">
				{/* Top Row: Section # + Name */}
				{/* Section # */}
				<div className="flex flex-col gap-2">
					<span className="text-2xs text-gray-400 uppercase tracking-wide sm:text-xs">
						Section {section.sectionNumber}
					</span>
					{/* Section Name */}
					<Editable
						value={sectionName}
						onChange={setSectionName}
						placeholder="Section name..."
						emptyValue="+ Add section name"
						valueButtonClassName="text-base sm:text-lg"
					/>
				</div>
				{/* 					<div className="mt-1 sm:mt-1.5">
						{isEditingName ? (
							<div className="flex items-stretch">
								<input
									ref={inputRef}
									type="text"
									value={sectionName}
									onChange={(e) => setSectionName(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											setIsEditingName(false);
										} else if (e.key === 'Escape') {
											setSectionName(section.name);
											setIsEditingName(false);
										}
									}}
									onBlur={() => {
										setIsEditingName(false);
									}}
									className="min-w-0 flex-1 rounded-l-md border border-gray-300 border-r-0 bg-white px-2.5 py-1.5 text-gray-900 text-sm leading-tight placeholder:font-normal placeholder:text-[0.8rem] placeholder:text-gray-400 focus:border-gray-400 focus:outline-none sm:px-3 sm:py-2 sm:text-base sm:placeholder:text-[0.9rem]"
									placeholder="Section name..."
								/>
								<button
									type="button"
									onMouseDown={(e) => {
										e.preventDefault(); // Prevent blur
										// TODO: Save section name logic here
										setIsEditingName(false);
									}}
									className="rounded-r-md border border-gray-300 bg-gray-100 px-2.5 font-medium text-[0.65rem] text-gray-700 transition-colors hover:bg-gray-200 sm:px-3 sm:text-[0.75rem]"
									title="Save"
								>
									Save
								</button>
							</div>
						) : (
							<>
								{sectionName ? (
									<button
										type="button"
										onClick={() => setIsEditingName(true)}
										className="text-left font-semibold text-base text-gray-900 leading-tight hover:text-gray-700 sm:text-lg"
									>
										{sectionName}
									</button>
								) : (
									<button
										type="button"
										onClick={() => setIsEditingName(true)}
										className="inline-flex rounded-md border border-gray-300 border-dashed px-2.5 py-1.5 font-medium text-[0.7rem] text-gray-400 transition-colors hover:border-gray-400 hover:text-gray-600 sm:px-3 sm:py-2 sm:text-[0.8rem]"
									>
										+ Add section name
									</button>
								)}
							</>
						)}
					</div>
				</div>
 */}
				{/* Bottom Row: Section Type and Configuration (Rounds/Time Cap) */}
				<div className="flex flex-wrap items-center justify-between gap-2 sm:gap-2.5">
					<div className="flex items-center gap-1.5 sm:gap-2">
						<VirtualSelect
							options={sectionTypeOptions}
							value={
								sectionTypeOptions.find((opt) => opt.value === section.sectionType.toLowerCase()) ??
								null
							}
							onChange={handleSectionTypeChange}
							enableVirtualization={false}
							nullable={false}
							buttonClassName="rounded min-h-7 bg-gray-100 px-2 py-0.5 font-medium text-[0.65rem] text-gray-700 transition-colors hover:bg-gray-200 sm:px-2.5 sm:py-1 sm:text-[0.75rem]"
							menuClassName="min-w-40"
						/>
					</div>
					{/* Horizontal Layout Row */}
					<div className="flex items-center gap-1.5 sm:gap-2">
						<button
							type="button"
							className="flex flex-1 items-center justify-between gap-2 rounded border border-gray-300 bg-white px-2.5 py-1.5 transition-colors hover:border-gray-400 sm:flex-none sm:px-3 sm:py-2"
						>
							<div className="flex items-center gap-1 sm:gap-1.5">
								<RotateCcw className="size-3 flex-shrink-0 text-gray-500 sm:size-3.5" />
								<span className="whitespace-nowrap text-[0.65rem] text-gray-500 sm:text-[0.75rem]">
									Rounds
								</span>
							</div>
							<span className="whitespace-nowrap font-bold text-[0.75rem] text-gray-900 sm:text-[0.85rem]">
								{section.rounds}
							</span>
						</button>
						<button
							type="button"
							className="flex flex-1 items-center justify-between gap-2 rounded border border-gray-300 bg-white px-2.5 py-1.5 transition-colors hover:border-gray-400 sm:flex-none sm:px-3 sm:py-2"
						>
							<div className="flex items-center gap-1 sm:gap-1.5">
								<Clock className="size-3 flex-shrink-0 text-gray-500 sm:size-3.5" />
								<span className="whitespace-nowrap text-[0.65rem] text-gray-500 sm:text-[0.75rem]">
									Time Cap
								</span>
							</div>
							<span className="whitespace-nowrap font-bold text-[0.75rem] text-gray-900 sm:text-[0.85rem]">
								{section.duration}
							</span>
						</button>
					</div>
				</div>
			</div>
		</header>
	);
}

function ExerciseCard({
	step,
	onToggle,
	onModeToggle,
	onMetricToggle,
	onDelete,
}: {
	step: StepPlan;
	onToggle: (stepId: string) => void;
	onModeToggle: (stepId: string) => void;
	onMetricToggle: (stepId: string, metricId: string) => void;
	onDelete: (stepId: string) => void;
}) {
	const selectedMetrics = step.metrics.filter((m) => m.isSelected);
	const selectedCount = selectedMetrics.length;
	const MAX_METRICS = 3;
	const [isEditingNote, setIsEditingNote] = useState(false);
	const [noteValue, setNoteValue] = useState(step.note);
	const [hasNote, setHasNote] = useState(!!step.note);

	// Muscle color mapping - very light colors matching design aesthetic
	const muscleColors: Record<string, string> = {
		Quads: 'bg-blue-100 text-blue-700',
		Hamstrings: 'bg-orange-100 text-orange-700',
		Chest: 'bg-red-100 text-red-700',
		Back: 'bg-purple-100 text-purple-700',
		Shoulders: 'bg-amber-100 text-amber-700',
		Arms: 'bg-pink-100 text-pink-700',
		Core: 'bg-emerald-100 text-emerald-700',
		Glutes: 'bg-rose-100 text-rose-700',
		'Full Body': 'bg-slate-100 text-slate-700',
	};

	const muscleColorClass = muscleColors[step.primaryMuscle] || 'bg-indigo-100 text-indigo-700';

	return (
		<article className="overflow-hidden border border-gray-200 border-t-0 bg-white first:border-t">
			{/* Exercise Header - Clickable */}
			<button
				type="button"
				onClick={() => onToggle(step.id)}
				className="flex w-full items-start gap-2 px-3.5 py-3 text-left transition-colors active:bg-gray-50 sm:gap-3 sm:px-4 sm:py-3.5"
			>
				<div className="mt-0.5 flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 font-semibold text-[0.7rem] text-white sm:size-7 sm:text-[0.8rem]">
					{step.order + 1}
				</div>
				<div className="min-w-0 flex-1">
					<h2 className="font-semibold text-gray-900 text-sm leading-tight sm:text-base">
						{step.movement}
					</h2>
					<div className="mt-1 flex flex-wrap items-center gap-1.5 sm:gap-2">
						<span className="text-[0.6rem] text-gray-400 uppercase tracking-wide sm:text-[0.7rem]">
							{step.equipment}
						</span>
						<span
							className={cn(
								'rounded-full px-1.5 py-0.5 font-medium text-[0.6rem] sm:px-2 sm:py-0.5 sm:text-[0.7rem]',
								muscleColorClass,
							)}
						>
							{step.primaryMuscle}
						</span>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onDelete(step.id);
						}}
						className="flex size-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 sm:size-8"
						title="Delete exercise"
					>
						<Trash2Icon className="size-3.5 sm:size-4" />
					</button>
					<ChevronDownIcon
						className={cn(
							'size-4 flex-shrink-0 text-gray-400 transition-transform sm:size-5',
							step.isExpanded && 'rotate-180',
						)}
					/>
				</div>
			</button>

			{/* Expanded Content */}
			{step.isExpanded ? (
				<div className="space-y-4 px-3.5 pt-3 pb-3.5 sm:space-y-5 sm:px-4 sm:pt-4 sm:pb-4">
					{/* Note - Editable with Delete Option */}
					<div>
						{isEditingNote ? (
							<div className="space-y-1.5 sm:space-y-2">
								<textarea
									value={noteValue}
									onChange={(e) => setNoteValue(e.target.value)}
									className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-[0.7rem] text-gray-700 leading-relaxed focus:border-gray-400 focus:outline-none sm:px-3 sm:py-2.5 sm:text-[0.8rem]"
									rows={3}
									placeholder="Add a note for this exercise..."
								/>
								<div className="flex gap-1.5 sm:gap-2">
									<button
										type="button"
										onClick={() => {
											// TODO: Save note logic here
											setHasNote(true);
											setIsEditingNote(false);
										}}
										className="rounded-md bg-gray-900 px-2.5 py-1 font-medium text-[0.65rem] text-white hover:bg-gray-800 sm:px-3 sm:py-1.5 sm:text-[0.75rem]"
									>
										Save
									</button>
									<button
										type="button"
										onClick={() => {
											setNoteValue(step.note);
											setIsEditingNote(false);
										}}
										className="rounded-md border border-gray-300 px-2.5 py-1 font-medium text-[0.65rem] text-gray-600 hover:bg-gray-50 sm:px-3 sm:py-1.5 sm:text-[0.75rem]"
									>
										Cancel
									</button>
								</div>
							</div>
						) : (
							<>
								{hasNote ? (
									<div className="flex gap-1.5 sm:gap-2">
										<button
											type="button"
											onClick={() => setIsEditingNote(true)}
											className="flex-1 rounded-lg bg-gray-50 px-2.5 py-2 text-left text-[0.7rem] text-gray-700 leading-relaxed transition-colors hover:bg-gray-100 sm:px-3 sm:py-2.5 sm:text-[0.8rem]"
										>
											{noteValue || step.note}
										</button>
										<button
											type="button"
											onClick={() => {
												setHasNote(false);
												setNoteValue('');
											}}
											className="flex size-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 sm:size-9"
											title="Delete note"
										>
											<Trash2Icon className="size-3.5 sm:size-4" />
										</button>
									</div>
								) : (
									<button
										type="button"
										onClick={() => setIsEditingNote(true)}
										className="w-full rounded-lg border border-gray-300 border-dashed px-2.5 py-1.5 text-left text-[0.7rem] text-gray-400 hover:border-gray-400 hover:text-gray-600 sm:px-3 sm:py-2 sm:text-[0.8rem]"
									>
										+ Add note
									</button>
								)}
							</>
						)}
					</div>

					{/* Set Mode Section - V2.10 Style */}
					<div>
						<div className="flex items-center justify-between">
							<span className="font-medium text-[0.65rem] text-gray-500 uppercase tracking-wide sm:text-[0.75rem]">
								Mode
							</span>
							<div className="inline-flex items-center rounded-full bg-gray-200 p-0.5 sm:p-1">
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										if (step.mode !== 'template') {
											onModeToggle(step.id);
										}
									}}
									className={cn(
										'rounded-full px-2.5 py-0.5 font-medium text-[0.6rem] transition-all sm:px-3 sm:py-1 sm:text-[0.7rem]',
										step.mode === 'template' ? 'bg-white text-gray-900' : 'text-gray-600',
									)}
								>
									Template
								</button>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										if (step.mode !== 'custom') {
											onModeToggle(step.id);
										}
									}}
									className={cn(
										'rounded-full px-2.5 py-0.5 font-medium text-[0.6rem] transition-all sm:px-3 sm:py-1 sm:text-[0.7rem]',
										step.mode === 'custom' ? 'bg-white text-gray-900' : 'text-gray-600',
									)}
								>
									Custom
								</button>
							</div>
						</div>
					</div>

					{/* Metrics Toggle Buttons - V2.10 Style with white checkmark in green circle */}
					<div className="space-y-2 sm:space-y-2.5">
						<div className="flex items-center gap-1.5 sm:gap-2">
							<span className="font-medium text-[0.65rem] text-gray-500 uppercase tracking-wide sm:text-[0.75rem]">
								Metrics
							</span>
							<span className="rounded-full bg-gray-200 px-1.5 py-0.5 font-semibold text-[0.55rem] text-gray-600 sm:px-2 sm:py-0.5 sm:text-[0.65rem]">
								Max {MAX_METRICS}
							</span>
						</div>
						<div
							className="flex gap-1 overflow-x-auto pb-2 sm:gap-1.5"
							style={{
								scrollbarWidth: 'none',
								msOverflowStyle: 'none',
								WebkitOverflowScrolling: 'touch',
							}}
						>
							<style>{`
								.overflow-x-auto::-webkit-scrollbar {
									display: none;
								}
							`}</style>
							{step.metrics
								.slice()
								.sort((a, b) => (b.isSelected ? 1 : 0) - (a.isSelected ? 1 : 0))
								.map((metric) => {
									const canSelect = selectedCount < MAX_METRICS || metric.isSelected;
									return (
										<button
											key={metric.id}
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												if (canSelect) {
													onMetricToggle(step.id, metric.id);
												}
											}}
											disabled={!canSelect}
											className={cn(
												'relative flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 font-medium text-[0.6rem] transition-all sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-[0.7rem]',
												metric.isSelected
													? 'border-gray-300 bg-white text-gray-900'
													: 'border-gray-200 bg-gray-100 text-gray-600',
												!canSelect && 'cursor-not-allowed opacity-50',
											)}
										>
											{metric.isSelected && (
												<span className="relative inline-flex size-3 items-center justify-center rounded-full bg-green-500 sm:size-3.5">
													<svg
														className="size-2 text-white sm:size-2.5"
														viewBox="0 0 12 12"
														fill="none"
														aria-hidden="true"
													>
														<path
															d="M10 3L4.5 8.5L2 6"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</span>
											)}
											{metric.name}
											{metric.unit && (
												<span className="text-[0.5rem] opacity-70 sm:text-[0.6rem]">
													{metric.unit}
												</span>
											)}
										</button>
									);
								})}
						</div>
					</div>

					{/* Sets Table */}
					<div className="overflow-hidden rounded-lg border border-gray-200">
						<table className="w-full">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-2.5 py-2 text-left font-semibold text-[0.6rem] text-gray-500 uppercase tracking-wide sm:px-3 sm:py-2.5 sm:text-[0.7rem]">
										Set
									</th>
									{selectedMetrics.map((metric) => (
										<th
											key={metric.id}
											className="px-2.5 py-2 text-center font-semibold text-[0.6rem] text-gray-500 uppercase tracking-wide sm:px-3 sm:py-2.5 sm:text-[0.7rem]"
										>
											{metric.name}
											{metric.unit ? (
												<span className="ml-0.5 text-[0.55rem] text-gray-400 sm:text-[0.65rem]">
													({metric.unit})
												</span>
											) : null}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{step.mode === 'template' ? (
									<tr className="border-gray-200 border-t">
										<td className="px-2.5 py-2.5 sm:px-3 sm:py-3">
											<div className="flex items-center gap-1.5 sm:gap-2">
												<div className="flex size-6 items-center justify-center rounded-full bg-gray-100 font-semibold text-[0.7rem] text-gray-700 sm:size-7 sm:text-[0.8rem]">
													1
												</div>
												<span className="text-[0.6rem] text-gray-500 italic sm:text-[0.7rem]">
													Template
												</span>
											</div>
										</td>
										{selectedMetrics.map((metric) => (
											<td
												key={`template-${metric.id}`}
												className="px-2.5 py-2.5 text-center font-medium text-[0.7rem] text-gray-800 sm:px-3 sm:py-3 sm:text-[0.8rem]"
											>
												{step.sets[0]?.values[metric.id] || '-'}
											</td>
										))}
									</tr>
								) : (
									step.sets.map((set) => (
										<tr key={set.id} className="border-gray-200 border-t">
											<td className="px-2.5 py-2.5 sm:px-3 sm:py-3">
												<div className="flex size-6 items-center justify-center rounded-full bg-gray-100 font-semibold text-[0.7rem] text-gray-700 sm:size-7 sm:text-[0.8rem]">
													{set.order + 1}
												</div>
											</td>
											{selectedMetrics.map((metric) => (
												<td
													key={`${set.id}-${metric.id}`}
													className="px-2.5 py-2.5 text-center font-medium text-[0.7rem] text-gray-800 sm:px-3 sm:py-3 sm:text-[0.8rem]"
												>
													{set.values[metric.id] || '-'}
												</td>
											))}
										</tr>
									))
								)}
								{/* Add Set Row */}
								<tr className="border-gray-200 border-t bg-white">
									<td
										colSpan={selectedMetrics.length + 1}
										className="px-2.5 py-2 sm:px-3 sm:py-2.5"
									>
										<button
											type="button"
											className="flex w-full items-center justify-center gap-1.5 font-medium text-[0.7rem] text-gray-600 uppercase tracking-wide transition-colors active:text-gray-900 sm:gap-2 sm:text-[0.8rem]"
										>
											<PlusIcon className="size-3.5 sm:size-4" /> Add Set
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			) : null}
		</article>
	);
}

export function StepEditorMobileDemo() {
	const [section, setSection] = useState<SectionPlan>(sectionPlan);
	const configurationItems = getConfigurationItems(section);

	const handleToggleStep = (stepId: string) => {
		setSection((prev) => ({
			...prev,
			steps: prev.steps.map((step) =>
				step.id === stepId
					? {
							...step,
							isExpanded: !step.isExpanded,
						}
					: step,
			),
		}));
	};

	const handleModeToggle = (stepId: string) => {
		setSection((prev) => ({
			...prev,
			steps: prev.steps.map((step) =>
				step.id === stepId
					? {
							...step,
							mode: step.mode === 'custom' ? 'template' : 'custom',
						}
					: step,
			),
		}));
	};

	const handleMetricToggle = (stepId: string, metricId: string) => {
		setSection((prev) => ({
			...prev,
			steps: prev.steps.map((step) =>
				step.id === stepId
					? {
							...step,
							metrics: step.metrics.map((metric) =>
								metric.id === metricId ? { ...metric, isSelected: !metric.isSelected } : metric,
							),
						}
					: step,
			),
		}));
	};

	const handleDeleteStep = (stepId: string) => {
		setSection((prev) => ({
			...prev,
			steps: prev.steps.filter((step) => step.id !== stepId),
		}));
	};

	return (
		<div className="bg-gray-100 pt-4 pb-8 sm:pt-6 sm:pb-10">
			<div className="mx-auto flex w-full max-w-2xl flex-col gap-6 px-3 sm:gap-8 sm:px-4">
				{/* Section Container - visually connects header with exercises */}
				<div className="overflow-hidden rounded-xl shadow-sm">
					{/* Section Header */}
					<SectionHeader section={section} />

					{/* Exercises Header - Connected to section */}
					<div className="flex items-center justify-between border border-gray-200 border-t-0 bg-white px-4 py-2 sm:px-5 sm:py-2.5">
						<h2 className="font-semibold text-[0.65rem] text-gray-500 uppercase tracking-wide sm:text-[0.75rem]">
							Exercises
						</h2>
						<div className="flex items-center gap-1.5 sm:gap-2">
							<button
								type="button"
								className="flex items-center gap-0.5 rounded-md border border-gray-300 bg-white px-2 py-1 font-medium text-[0.65rem] text-gray-700 transition-colors hover:border-gray-400 hover:bg-gray-50 sm:gap-1 sm:px-2.5 sm:py-1.5 sm:text-[0.75rem]"
							>
								+ Add Rest
							</button>
							<button
								type="button"
								className="flex items-center gap-0.5 rounded-md bg-gray-900 px-2 py-1 font-medium text-[0.65rem] text-white transition-colors active:bg-gray-800 sm:gap-1 sm:px-2.5 sm:py-1.5 sm:text-[0.75rem]"
							>
								+ Add
							</button>
						</div>
					</div>
					{/* Exercise Cards - No gap, stacked continuously */}
					{section.steps.map((step) => (
						<ExerciseCard
							key={step.id}
							step={step}
							onToggle={handleToggleStep}
							onModeToggle={handleModeToggle}
							onMetricToggle={handleMetricToggle}
							onDelete={handleDeleteStep}
						/>
					))}
				</div>

				{/* Add Section Button */}
				<button
					type="button"
					className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-300 border-dashed bg-white px-4 py-3 font-medium text-[0.75rem] text-gray-600 uppercase tracking-wide transition-colors active:bg-gray-50 sm:gap-2 sm:px-5 sm:py-3.5 sm:text-[0.85rem]"
				>
					<PlusIcon className="size-4 sm:size-4.5" /> Add Section
				</button>

				{/* Configuration Button Design Explorations */}
				<div className="rounded-xl border border-gray-200 bg-white/80 p-3 sm:p-4">
					<div className="flex items-baseline justify-between">
						<h3 className="font-semibold text-[0.65rem] text-gray-500 uppercase tracking-wide sm:text-[0.75rem]">
							Configuration Buttons
						</h3>
						<span className="text-[0.55rem] text-gray-400 sm:text-[0.6rem]">
							12 layouts · Tap to compare
						</span>
					</div>
					<div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
						{configurationVariants.map((variant, index) => (
							<div
								key={variant.id}
								className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-2"
							>
								<span className="font-medium text-[0.55rem] text-gray-400 uppercase tracking-wide">
									{String(index + 1).padStart(2, '0')} · {variant.name}
								</span>
								{variant.render(configurationItems)}
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
